import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Modal, ModalProps } from "../Modal";

const Context = createContext<
  | {
      stack: string[];
      push: (id: string) => void;
      pop: (id: string) => void;
    }
  | undefined
>(undefined);

const last = (id: string, stack: string[]) => {
  return stack.length > 0 && stack[stack.length - 1] === id;
};

const Provider = ({ children }: { children?: ReactNode }) => {
  const [stack, setStack] = useState<string[]>([]);

  const push = useCallback((id: string) => {
    setStack((p) => p.concat([id]));
  }, []);

  const pop = useCallback((id: string) => {
    setStack((p) => {
      if (last(id, p)) {
        return p.slice(0, p.length - 1);
      }

      return p;
    });
  }, []);

  return (
    <Context.Provider value={{ stack, push, pop }}>{children}</Context.Provider>
  );
};

export const StackedModal = (props: PropsWithChildren<ModalProps>) => {
  const id = useRef((Math.random() + 1).toString(36).substring(7));
  const context = useContext(Context);

  useEffect(() => {
    if (!context) return;
    const currentId = id.current;

    if (props.isOpen) {
      context.push(currentId);
    } else {
      context.pop(currentId);
    }
    return () => context.pop(currentId);
  }, [props.isOpen]);

  if (context) {
    return (
      <Modal
        {...props}
        withBackdropClose={
          last(id.current, context.stack) ? props.withBackdropClose : false
        }
      />
    );
  }

  return (
    <Provider>
      <StackedModal {...props} />
    </Provider>
  );
};
